import React, { forwardRef, useMemo } from 'react';
import PDFPage from '../PDFPage';
import styles from '../PDFReportPreview.module.css';
import { RevenueRentalType } from 'types/TransactionReport';
import {
    getSelectableRentalTypes,
    rentalTypeOptions,
    reportHasTicketRevenue,
    resolveSelectedRentalTypes,
} from 'components/RevenueWidgets/helpers';
import { useRevenueContext } from 'contexts/RevenueContext';
import AGTAbleWrapper from 'components/AGTable/AGTableWrapper';
import { getByRentalTypeTableData } from './utils';
import { getLabelFromOptions } from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';

type ByRentalTypePageProps = {
    spotAddress: string;
    dateTime: string;
    chartCanvas: HTMLCanvasElement | null;
    filters: { rentalType: RevenueRentalType };
};

const ByRentalTypePage = forwardRef<HTMLDivElement, ByRentalTypePageProps>(function ByRentalTypePage(
    { spotAddress, dateTime, chartCanvas, filters },
    ref
) {
    const { revenueByRentalTypeReport } = useRevenueContext();
    const hasTicketRevenue = reportHasTicketRevenue(revenueByRentalTypeReport);

    const tableData = useMemo(
        () =>
            getByRentalTypeTableData({
                revenueByRentalTypeReport,
                rentalTypeFilter: filters.rentalType,
                hasTicketRevenue,
            }),
        [revenueByRentalTypeReport, filters.rentalType, hasTicketRevenue]
    );

    // Resolved the same way as the table's columns, so the summary can never claim a filter
    // the table didn't apply.
    const filtersInfo = useMemo(() => {
        const selected = resolveSelectedRentalTypes(filters.rentalType, hasTicketRevenue);
        const isAll = selected.length === getSelectableRentalTypes(hasTicketRevenue).length;
        return [
            {
                label: 'Rental Type',
                value: isAll ? 'All' : selected.map((rt) => getLabelFromOptions(rentalTypeOptions, rt)).join(', '),
            },
        ];
    }, [filters.rentalType, hasTicketRevenue]);

    return (
        <PDFPage
            ref={ref}
            spotAddress={spotAddress}
            dateTime={dateTime}
            title="Gross Revenue by Rental Type"
            filters={filtersInfo}
        >
            {chartCanvas ? (
                <div className={styles.contentContainer}>
                    <img
                        src={chartCanvas.toDataURL('image/png')}
                        alt="Gross Revenue by Rental Type"
                        className={styles.capturedChart}
                    />
                    <div className={styles.pdfTableContainerCompact}>
                        <AGTAbleWrapper className={styles.firstColumnColored}>
                            <thead>
                                <tr>
                                    {tableData.headers.map((header) => (
                                        <th key={header}>{header}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {tableData.rows.map((row, rowIndex) => (
                                    <tr key={rowIndex}>
                                        {row.map((cell, cellIndex) => (
                                            <td key={cellIndex}>{cell}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </AGTAbleWrapper>
                    </div>
                </div>
            ) : (
                <div className={styles.noDataMessage}>
                    <p>No data available for this chart</p>
                </div>
            )}
        </PDFPage>
    );
});

export default ByRentalTypePage;
